import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Input from "components/Elements/Input";
import Select from "react-select";
import InputField from "components/InputField/InputField";
import {
  saveSupportingContent,
  clearSavedSupportingContent,
} from "actions/agents";
import { getSupportingContentWorkflows } from "actions/workflows";
import styled from "styled-components";

export const ErrorField = styled.span`
  color: red;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const AddSupportingContentModal = NiceModal.create(({ agent }) => {
  const dispatch = useDispatch();
  const { savingSupportingContent } = useSelector((state) => state.agents);
  const { supportingContentWorkflows } = useSelector(
    (state) => state.workflows
  );
  const modal = useModal();
  const formRef = useRef(null);
  const [workflowInputParams, setWorkflowInputParams] = useState({});
  const [supportingContent, setSupportingContent] = useState({ data: {} });
  const [
    selectedSupportingContentWorkflow,
    setSelectedSupportingContentWorkflow,
  ] = useState(null);
  const [
    supportingContentWorkflowOptions,
    setSupportingContentWorkflowOptions,
  ] = useState([]);

  useEffect(() => {
    if (supportingContentWorkflows) {
      const options = supportingContentWorkflows.map((w) => {
        return { value: w.public_id, label: w.name };
      });
      setSupportingContentWorkflowOptions(options);
    } else {
      dispatch(getSupportingContentWorkflows());
    }
    // eslint-disable-next-line
  }, [supportingContentWorkflows]);

  const hide = (e) => {
    e.stopPropagation();
    dispatch(clearSavedSupportingContent());
    modal.remove();
  };

  const [formErrors, setFormErrors] = useState({});
  const handleChange = ({ target }) => {
    const u = { ...supportingContent };

    if (target.type === "checkbox") {
      u[target.name] = target.checked;
    } else {
      u[target.name] = target.value;
    }
    setSupportingContent(u);
  };

  const handleInputFieldChange = (name, value) => {
    const u = { ...supportingContent };
    u.data[name] = value;
    setSupportingContent(u);
  };

  const handleSupportingContentWorkflowChange = (supportingContentWorkflow) => {
    const m = { ...supportingContent };
    m.workflow_id = supportingContentWorkflow.value;
    m.workflow = supportingContentWorkflows.find(
      (w) => w.public_id === supportingContentWorkflow.value
    );
    console.log("WORKFLOW: ", supportingContentWorkflow.value, m.workflow);
    setWorkflowInputParams(
      m.workflow.input_params ? JSON.parse(m.workflow.input_params) : {}
    );
    setSupportingContent(m);
    setSelectedSupportingContentWorkflow(supportingContentWorkflow);
  };

  const validateFields = () => {
    setFormErrors({});
    const errors = {};

    const urlValid =
      !supportingContent.url || supportingContent.url.match(/^http(s):\/\/.*$/);
    errors.url = urlValid ? "" : " is invalid";
    const nameValid =
      supportingContent.name && supportingContent.name.length > 0;

    setFormErrors(errors);

    return nameValid;
  };

  const save = () => {
    if (validateFields()) {
      dispatch(saveSupportingContent(supportingContent, agent.public_id));
    }
  };

  const displaySupportingContentTypeFields = () => {
    return Object.keys(workflowInputParams).map((key) => {
      const param = workflowInputParams[key];
      return (
        <div key={`inputfield_${key}`}>
          <InputField
            name={key}
            param={param}
            media={agent.media}
            value={supportingContent.data[key] ?? ""}
            onChange={handleInputFieldChange}
          />
        </div>
      );
    });
  };

  return (
    <>
      <div className="w-9/12 h-auto force-height-90 force-center items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-full my-6 mx-auto max-w-3xl">
          {/*content*/}
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            {/*header*/}
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-3xl font-semibold">Add Supporting Content</h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                onClick={hide}
              >
                <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            {/*body*/}
            <div className="relative p-6 flex-auto ">
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <form ref={formRef}>
                  <label>
                    Name <ErrorField>{formErrors.name}</ErrorField>
                  </label>
                  <Input
                    label="Name"
                    placeholder="Name"
                    name="name"
                    value={supportingContent.name ?? ""}
                    onChange={handleChange}
                  />
                  <label>
                    Description{" "}
                    <ErrorField>{formErrors.description}</ErrorField>
                  </label>
                  <Input
                    type="textarea"
                    label="Description"
                    placeholder="Description"
                    name="description"
                    value={supportingContent.description ?? ""}
                    onChange={handleChange}
                  />
                  <label>
                    Content Support Workflow{" "}
                    <ErrorField>{formErrors.workflow}</ErrorField>
                  </label>
                  <Select
                    label="Workflow"
                    placeholder="Choose a Workflow"
                    name="workflow"
                    value={selectedSupportingContentWorkflow}
                    options={supportingContentWorkflowOptions}
                    onChange={handleSupportingContentWorkflowChange}
                  />
                  {displaySupportingContentTypeFields()}
                </form>
              </div>
            </div>
            <div className="relative p-6 flex-auto"></div>
            {/*footer*/}
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              {!savingSupportingContent ? (
                <button
                  className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={hide}
                >
                  Close
                </button>
              ) : null}
              <button
                className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={save}
                disabled={savingSupportingContent}
              >
                {savingSupportingContent ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i> Saving...
                  </>
                ) : (
                  <>Save</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
});

export default AddSupportingContentModal;
